import React, { createContext, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getSessionId } from '../../utils/session-utils';
import { useMessageHistory } from '../../hooks/useMessageHistory';
import { useAssistantBot } from '../../hooks/useAssistantBot';
import { MESSAGE_ORIGIN_UNSPECIFIED } from './constants';

const AssistantContext = createContext({
  historyLoading: false,
  messages: [],
  saveMessage: () => {},
  clearHistory: () => {},
  setFeedbackProvided: () => {},
});

// Create a provider component
/**
 * @param { AssistantProviderProps } props
 * @returns { React.Provider<AssistantContextProps> }
 */
export const AssistantProvider = ({ withPersistence, children }) => {
  const {
    isHistoryAvailable,
    messages,
    saveMessage,
    historyLoading,
    clearHistory,
    setFeedbackProvided,
    broadCastMessageUpdate,
    drawerShouldOpenOnLoad,
    hasSessionInteractedWithFab,
    updateSession
  } = useMessageHistory(withPersistence);
  const [submitUserQuestionToApi, { data: botResponse, loading: botLoading, error: botError }] = useAssistantBot();

  const askUserQuestion = ({ userQuestion, messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED }) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.submit-click', {
      sessionID: getSessionId(),
      question: `${messageOrigin}: ${userQuestion}`,
    });
    saveMessage({ type: 'user', content: userQuestion })
      .then((savedUserMessage) => {
        return submitUserQuestionToApi({
          userQuestion: savedUserMessage.content,
          timestamp: savedUserMessage.timestamp
        });
      })
      .catch(() => {});
  };

  // Waits for result of submitUserQuestionToApi in askUserQuestion before saving bot response
  // Unable to promise chain useLazyQuery trigger function from submitUserQuestionToApi so we wait for the useEffect
  useEffect(() => {
    if (!botLoading && (botResponse || botError)) {
      const botContent = {
        type: 'bot',
        content: botError ? null : { ...(botResponse?.retrieveDetails || {}) },
        error: botError
      };
      saveMessage(botContent)
        .catch(() => {})
        .finally(broadCastMessageUpdate);
    }
  }, [botLoading]);

  const context = {
    askUserQuestion,
    isHistoryAvailable,
    messages,
    saveMessage,
    historyLoading,
    clearHistory,
    setFeedbackProvided,
    botLoading,
    drawerShouldOpenOnLoad,
    hasSessionInteractedWithFab,
    updateSession
  };

  return (
    <AssistantContext.Provider value={context}>
      {children}
    </AssistantContext.Provider>
  );
};

AssistantProvider.propTypes = {
  children: PropTypes.node.isRequired,
  withPersistence: PropTypes.bool,
};

AssistantProvider.defaultProps = {
  withPersistence: true,
};

// Custom hook to use the AssistantContext
/**
 * @returns { AssistantContextProps }
 */
export const useAssistant = () => {
  return useContext(AssistantContext);
};
